import type { DirectoryUser } from '@jarvis/platform-ui';

export const GRID_STATE_KEY = 'webopsui:hvg-clients-grid:v7';
export const GRID_SCHEMA_VERSION = 7;

export const GRID_COLUMNS = [
  'id',
  'displayName',
  'userPrincipalName',
  'mail',
  'accountEnabled',
  'department',
  'jobTitle',
  'company',
  'createdDateTime',
  'givenName',
  'surname',
  'employeeType',
  'employeeHireDate',
  'officeLocation',
  'businessPhone'
] as const;

export type ClientUserGridRow = DirectoryUser;

export function sortRows(rows: ClientUserGridRow[]): ClientUserGridRow[] {
  return [...rows].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' })
  );
}

/** Sanitize account name (local part / mail nickname). */
export function sanitizeAccountName(value: string): string {
  return value.trim().replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 64);
}

export function buildUpn(accountName: string, domain: string): string {
  const local = sanitizeAccountName(accountName);
  const d = domain.trim();
  if (!local || !d) return '';
  return `${local}@${d}`;
}

export function splitUpn(userPrincipalName: string): { accountName: string; domain: string } {
  const at = userPrincipalName.indexOf('@');
  if (at <= 0) return { accountName: userPrincipalName.trim(), domain: '' };
  return {
    accountName: userPrincipalName.slice(0, at).trim(),
    domain: userPrincipalName.slice(at + 1).trim()
  };
}

export function toOptimisticUserRow(
  created: {
    id: string;
    displayName: string;
    userPrincipalName: string;
    mail: string | null;
    accountEnabled: boolean;
    department?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    givenName?: string | null;
    surname?: string | null;
    employeeType?: string | null;
    employeeHireDate?: string | null;
    officeLocation?: string | null;
    businessPhone?: string | null;
    createdDateTime?: string | null;
  },
  tenantId: string
): ClientUserGridRow {
  return {
    id: created.id,
    displayName: created.displayName,
    userPrincipalName: created.userPrincipalName,
    mail: created.mail,
    accountEnabled: created.accountEnabled,
    company: created.company ?? null,
    department: created.department ?? null,
    jobTitle: created.jobTitle ?? null,
    givenName: created.givenName ?? null,
    surname: created.surname ?? null,
    employeeType: created.employeeType ?? null,
    employeeHireDate: created.employeeHireDate ?? null,
    officeLocation: created.officeLocation ?? null,
    businessPhone: created.businessPhone ?? null,
    employeeId: null,
    mobilePhone: null,
    syncedAt: new Date().toISOString(),
    isPresent: true,
    tenantId,
    tenantDisplayName: null,
    createdDateTime: created.createdDateTime ?? new Date().toISOString(),
    hasP2License: false
  };
}
